import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { cx, randomString } from './helpers';
import { PlainIcon } from './icons';
import { useIcon } from './theme';

type CheckBoxProps = Partial<PropsWithChildren> & {
  checked?: boolean;
  disabled?: boolean;
  id?: string;
  onChange: (value: boolean) => void;
  readOnly?: boolean;
  required?: boolean;
  ariaLabel?: string | null;
  label?: string | null;
  className?: string;
};

export function CheckBox({
  ariaLabel,
  checked = false,
  disabled = false,
  id,
  label,
  onChange,
  readOnly = false,
  required = false,
  children,
  className,
}: CheckBoxProps): JSX.Element {
  const icon = useIcon('checkBox');
  const checkBoxId = useMemo(() => id || randomString(), [id]);
  const [isChecked, setIsChecked] = useState(checked);

  useEffect(() => {
    setIsChecked(checked);
  }, [checked]);

  return (
    <label
      className={cx(
        'aura--checkbox',
        { disabled, 'read-only': readOnly, checked: isChecked },
        className,
      )}
      aria-label={ariaLabel || label || undefined}
      htmlFor={checkBoxId}
    >
      {isChecked && (
        <PlainIcon
          color=""
          size={0}
          backgroundColor=""
          name={icon}
          className="aura--checkbox-checked"
        />
      )}
      {!isChecked && <div className="aura--checkbox-empty" />}
      <input
        disabled={disabled}
        id={checkBoxId}
        checked={isChecked}
        onChange={e => {
          setIsChecked(e.currentTarget.checked);
          onChange(e.currentTarget.checked);
        }}
        readOnly={readOnly}
        type="checkbox"
        required={required}
      />
      {children ||
        (label && <span className="aura--checkbox-label">{label}</span>)}
    </label>
  );
}
